<template>
    <vue-select
        ref="vueSelectSearchClient"
        placeholder="Search clients"
        v-model="query"
        :options="options"
        :filter="filter"
        :clearable="false"
        @search="search"
        @input="onSelect"
        :closeOnSelect="false"
        @close="onClose"
        class="search-client"
    >
        <template v-slot:option="{ fullName, index }">
            <b>{{ processName(fullName) }} ({{ index }})</b>
        </template>

        <template v-slot:no-options="{ search }">
            <div class="no-opt">
                <span v-if="search === ''">
                    Type to search
                </span>
                <span v-else-if="isPreloading">
                    Loading...
                </span>
                <span v-else>
                    No result
                </span>
            </div>
        </template>

        <template v-slot:open-indicator>
            <!-- this empty div is intentional, do not remove. this is for a "replacement" to default arrow -->
            <div></div>
        </template>

        <template v-slot:spinner="{ loading }">
            <img
                v-if="loading"
                class="spinner"
                src="/apps/assets/img/ajax-spinner.gif"
            />
        </template>

        <template v-slot:list-footer>
            <li ref="footerLoader" class="footerloader" v-if="hasNextPage">
                Loading...
            </li>
        </template>
    </vue-select>
</template>

<script>
// TODO: after search (user select patient), re-set tab position to the first tab so the lower content shows records that belongs to the patient
import vueSelect from "vue-select";
import { debounce } from "lodash";

const CLIENT_SEARCH_URI = `${process.env.BARRAMUNDI}actor/client-search?include_inactive=true`;

export default {
    components: {
        vueSelect
    },
    props: {
        focusOnload: {
            default: false,
            type: Boolean
        }
    },
    watch: {
        options(options = []) {
            let maxLength = 0;
            let ul = document.querySelector(".search-client ul");

            if (options.length === 0) {
                ul.style.width = "";
                ul.style.left = "";
                ul.style.right = "";
                ul.style.borderTopLeftRadius = "";
                ul.style.borderTopRightRadius = "";
            } else {
                for (let option of options) {
                    let labelLength = `${option.fullName} ${option.index}`
                        .length;

                    if (labelLength > maxLength) {
                        maxLength = labelLength;
                    }
                }

                // capped maxlength to 350 , because it will go out of screen for mobile
                let maxLengthValue = maxLength * 11;
                ul.style.width = `${
                    maxLengthValue < 350 ? maxLengthValue : 350
                }px`;
                ul.style.left = "unset";
                ul.style.right = "0";
                ul.style.borderTopLeftRadius = "5px";
                ul.style.borderTopRightRadius = "5px";
            }
        }
    },
    data() {
        return {
            query: "",
            options: [],
            observer: null,
            hasNextPage: false,
            isPreloading: false,
            resultMeta: null
        };
    },
    mounted() {
        this.observer = new IntersectionObserver(this.infiniteScroll);

        if (this.focusOnload) {
            this.focus();
        }
    },
    methods: {
        focus() {
            this.$refs.vueSelectSearchClient.$el.querySelector("input").focus();
        },
        search(value, loading) {
            this.isPreloading = true;
            this.query = value;

            if (value === "") {
                this.isPreloading = false;
                this.options = [];
                this.hasNextPage = false;
                return;
            }

            this.fetchOptions(value, this, loading);
        },
        fetchOptions: debounce(async (value, self, loading) => {
            loading(true);

            const res = await self.$http.get(CLIENT_SEARCH_URI, {
                params: {
                    query: value
                }
            });

            const { message, resource, meta } = res.data.response;

            if (message.code === 200) {
                self.options = resource;
                self.resultMeta = meta;

                if (self.options.length < meta.total) {
                    self.hasNextPage = true;
                } else {
                    self.hasNextPage = false;
                }
            } else {
                self.options = [];
                self.resultMeta = null;
            }

            loading(false);
            self.isPreloading = false;
            await self.registerFootLoader();
        }, 350),
        filter(options, search) {
            /**
             * if you ever wonder what this function does,
             * it basically loops through all options available
             * and then combine firstname, lastname and email of each option
             * to become a joined string, eg: "firstlastemail"
             * then it'll compare the search value with the joined string
             * and if it returns > -1, means the particular option
             * will be displayed in the dropdown list, and vice versa.
             */
            // prettier-ignore
            return options.filter(option => {
                let { firstname, lastname, fullName, email, home, mobile, work, fax, index } = option;
                let plainHome = (home || "").replace(/[^a-z0-9]/gi, "");
                let plainMobile = (mobile || "").replace(/[^a-z0-9]/gi, "");
                let plainWork = (work || "").replace(/[^a-z0-9]/gi, "");

                let label = [firstname, lastname, fullName, email, home, mobile, work, fax, index, plainMobile, plainHome, plainWork].join("");
                return (label || "").toLowerCase().indexOf(search.toLowerCase()) > -1;
            });
        },
        onSelect(value) {
            if (!value) {
                return;
            }

            const { encoded_id } = value;
            this.$router.push(`/client/profile/${encoded_id}`);
            this.query = "";
            this.options = [];
            window.scrollTo(0, 0); // reset scroll position
        },
        async registerFootLoader() {
            if (this.hasNextPage) {
                await this.$nextTick();
                this.observer.observe(this.$refs.footerLoader);
            }
        },
        onOpen() {
            this.registerFootLoader();
        },
        onClose() {
            this.observer.disconnect();
            this.options = []; // clear
            this.hasNextPage = false;
        },
        async infiniteScroll([{ isIntersecting, target }]) {
            const self = this;

            // references: https://vue-select.org/guide/infinite-scroll.html
            if (isIntersecting && this.hasNextPage) {
                const ul = target.offsetParent;
                const scrollTop = target.offsetParent.scrollTop;
                await this.$nextTick();

                this.fetchNextPage(async () => {
                    await self.$nextTick();
                    ul.scrollTop = scrollTop; // keep scroll position after we append the options
                });
            }
        },
        async fetchNextPage(callback) {
            if (!this.resultMeta) {
                return;
            }

            const { currentPage, lastPage } = this.resultMeta;

            if (currentPage >= lastPage) {
                this.hasNextPage = false;
                return;
            }

            const res = await this.$http.get(CLIENT_SEARCH_URI, {
                params: {
                    page: currentPage + 1,
                    query: this.query
                }
            });

            const { message, meta, resource } = res.data.response;

            if (message.code === 200) {
                this.resultMeta = meta;
                this.options = [...this.options, ...resource];

                if (this.options.length < meta.total) {
                    this.hasNextPage = true;
                } else {
                    this.hasNextPage = false;
                }

                if (callback) {
                    callback();
                }
            } else {
                this.hasNextPage = false;
            }
        },
        processName(fullName) {
            if (fullName.length > 35) {
                return fullName.slice(0, 35) + "... ";
            }

            return fullName;
        }
    }
};
</script>

<style scoped>
.no-opt {
    padding: 0 5px;
}
.footerloader {
    padding: 3px 20px;
    font-style: italic;
}
</style>
